import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, Optional } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router, RouterModule } from '@angular/router';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { SwitchProfileOverlayComponent } from '@rma/authentication/private/ui-switch-profile-overlay';
import { AuthService } from '@rma/authentication/public/api-authentication';
import {
  AccessibleProfile,
  ContextSelectorService,
  type CurrentProfileContext,
  DashboardProfileContextService,
  DashboardUserContextService,
  ProfileAccessContextService,
  type SelectedContext,
  UserType,
} from '@rma/authentication/public/user';
import { ImageOptimisePipe } from '@rma/common-ui-ng/images';
import { ProfileType } from '@rma/generic/domain/to-be-sorted';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { RmaModalService } from '@rma/generic/ui/modal';
import { getCurrentRoute, setRedirectUrl } from '@rma/generic/util/router-utils';
import { TeamCalloutService } from '@rma/marketing/public/ui-team-callout-banner';
import { BrandingFacadeService } from '@rma/profile/public/api-branding';
import { combineLatest, delay, map, Observable, take, tap } from 'rxjs';

export interface SwitchProfileUser {
  code: string;
  type: typeof UserType.Agent | typeof UserType.Agency;
  name: string;
  backgroundColour?: string;
  profileImage: string;
  profileType: ProfileType;
  index: number | null;
}

const scope = 'accountActions';

@Component({
  selector: 'rma-account-actions-component',
  templateUrl: 'account-actions.component.html',
  styleUrls: ['account-actions.component.scss'],
  imports: [
    AsyncPipe,
    ImageOptimisePipe,
    MatButtonModule,
    MatDialogModule,
    NgTemplateOutlet,
    RmaIconComponent,
    RouterModule,
    RouterModule,
    TranslocoModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideTranslocoScope(scope)],
})
export class AccountActionsModalComponent {
  protected readonly scope = scope;
  protected readonly teamsQueryParams$: Observable<Params>;

  public readonly currentProfile$: Observable<CurrentProfileContext | null>;
  public readonly hasMultipleProfiles$: Observable<boolean>;
  public readonly profiles$: Observable<SwitchProfileUser[]>;
  public readonly showAddTeamAccount$: Observable<boolean>;
  public readonly userBackgroundColour$: Observable<string | undefined>;

  public readonly hideActions: boolean = false;

  public constructor(
    private readonly authenticationService: AuthService,
    private readonly brandingFacade: BrandingFacadeService,
    private readonly contextSelector: ContextSelectorService,
    private readonly dialogRef: MatDialogRef<AccountActionsModalComponent>,
    private readonly profileContextService: DashboardProfileContextService,
    private readonly profileAccessContextService: ProfileAccessContextService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly teamCalloutService: TeamCalloutService,
    private readonly userContextService: DashboardUserContextService,
    private readonly modalService: RmaModalService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: { hideActions?: boolean },
  ) {
    this.hideActions = !!data?.hideActions;

    this.teamsQueryParams$ = this.route.queryParams.pipe(
      map((queryParams) => ({ source: 'teams', ...setRedirectUrl(getCurrentRoute(this.router), queryParams) })),
    );

    this.currentProfile$ = this.profileContextService.profileContext$;
    this.hasMultipleProfiles$ = this.userContextService.hasMultipleProfiles$;

    this.profiles$ = combineLatest([
      this.profileAccessContextService.getUsersAccessibleProfiles(),
      this.profileContextService.profileIdentifier$,
    ]).pipe(map(([profiles, profileId]) => this.mapAccessibleUsersToSwitchProfileUser(profiles, profileId)));

    this.userBackgroundColour$ = this.brandingFacade.selectedTheme$.pipe(map((x) => x.backgroundColour));

    this.showAddTeamAccount$ = this.teamCalloutService.showTeamsCallout$;
  }

  public switchUser(user: SwitchProfileUser): void {
    this.dialogRef.close();

    const switchProfileOverlay = this.modalService.openWithBlurBackground(SwitchProfileOverlayComponent, 'rma-dashboard', {
      data: { name: user.name, image: user.profileImage },
      enterAnimationDuration: '400ms',
      exitAnimationDuration: '400ms',
      panelClass: ['switch-profile-overlay'],
      disableClose: true,
    });

    this.contextSelector.setContext({ profileCode: user.code, profileType: user.profileType });

    this.profileContextService.profileContextChanged$
      .pipe(
        take(1),
        tap(() => {
          const paths = user.type === UserType.Agent ? ['/agency', '/leasing', '=Leasing'] : ['/agent'];
          if (paths.some((p) => location.href.indexOf(p) > -1)) {
            this.router.navigate(['/']);
          }
        }),
        delay(1000),
      )
      .subscribe(() => switchProfileOverlay.close());
  }

  protected onLogout(): void {
    this.authenticationService
      .logout()
      .pipe(take(1))
      .subscribe(() => (window.location.href = '/'));
  }

  private mapAccessibleUsersToSwitchProfileUser(
    profiles: AccessibleProfile[],
    selectedContext: SelectedContext | undefined,
  ): SwitchProfileUser[] {
    if (profiles.length === 0) {
      return [] as SwitchProfileUser[];
    }

    const mappedProfile: SwitchProfileUser[] = profiles.map((profile) => ({
      code: profile.profileCode,
      type: profile.userType,
      name: profile.profileName,
      profileImage: profile.profileImage,
      backgroundColour: profile.backgroundColour,
      profileType: profile.profileType,
      index: profile.index,
    }));

    const agents = mappedProfile.filter((profile) => profile.type === UserType.Agent);
    const agencies = mappedProfile.filter((profile) => profile.type === UserType.Agency);

    const combined = [...agents, ...agencies];

    return !selectedContext
      ? combined
      : combined.filter(
          (profile) => !(selectedContext.profileType === profile.profileType && selectedContext.profileCode === profile.code),
        );
  }
}
